import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { contentProtectionSx } from '../../../hooks/useContentProtection';
import { FullscreenContentContext, useFullscreenContent } from './FullscreenContentContext';

const ContentProtectionWatermark = ({ label, light = false }) => {
  if (!label) {
    return null;
  }

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 1,
      }}
    >
      {Array.from({ length: 8 }).map((_, row) => (
        <Box
          key={`wm-row-${row}`}
          sx={{
            position: 'absolute',
            top: `${row * 14}%`,
            left: '-20%',
            width: '140%',
            transform: 'rotate(-24deg)',
            display: 'flex',
            justifyContent: 'space-around',
            opacity: light ? 0.06 : 0.08,
            color: light ? '#ffffff' : '#111827',
            fontSize: { xs: '0.75rem', md: '0.95rem' },
            fontWeight: 700,
            letterSpacing: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {Array.from({ length: 5 }).map((__, col) => (
            <span key={`wm-${row}-${col}`}>{label}</span>
          ))}
        </Box>
      ))}
    </Box>
  );
};

/**
 * Wraps lesson content with a fullscreen toggle (browser Fullscreen API).
 * In fullscreen: edge-to-edge layout on desktop and mobile (100dvh).
 */
export default function ContentFullscreenFrame({
  title,
  subtitle,
  watermark,
  children,
  showFullscreenControl = true,
}) {
  const frameRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const syncFullscreenState = useCallback(() => {
    const active = document.fullscreenElement === frameRef.current;
    setIsFullscreen(active);
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', syncFullscreenState);
    return () => document.removeEventListener('fullscreenchange', syncFullscreenState);
  }, [syncFullscreenState]);

  const toggleFullscreen = async () => {
    const node = frameRef.current;
    if (!node) {
      return;
    }

    try {
      if (document.fullscreenElement === node) {
        await document.exitFullscreen();
      } else {
        await node.requestFullscreen();
      }
    } catch (error) {
      console.warn('Fullscreen request failed:', error);
    }
  };

  return (
    <FullscreenContentContext.Provider value={isFullscreen}>
      <Box
        ref={frameRef}
        className="lms-content-frame"
        sx={{
          position: 'relative',
          '&:fullscreen, &:-webkit-full-screen': {
            display: 'flex',
            flexDirection: 'column',
            width: '100vw',
            height: '100dvh',
            maxWidth: '100vw',
            maxHeight: '100dvh',
            bgcolor: '#ffffff',
            overflow: 'hidden',
            p: 0,
            m: 0,
          },
        }}
      >
        {showFullscreenControl ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{
              flexShrink: 0,
              mb: isFullscreen ? 0 : 2,
              position: 'relative',
              zIndex: 3,
              ...(isFullscreen
                ? {
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 1, sm: 1.25 },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#f8fafc',
                    minHeight: { xs: 48, sm: 52 },
                  }
                : {}),
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {subtitle ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: 'block' }}
                >
                  {subtitle}
                </Typography>
              ) : null}
              {title ? (
                <Typography variant={isFullscreen ? 'subtitle1' : 'body2'} fontWeight={700} noWrap>
                  {title}
                </Typography>
              ) : null}
            </Box>
            <Tooltip title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}>
              <IconButton
                onClick={toggleFullscreen}
                size="small"
                sx={{
                  flexShrink: 0,
                  bgcolor: isFullscreen ? 'rgba(15,23,42,0.06)' : 'action.hover',
                  '&:hover': {
                    bgcolor: isFullscreen ? 'rgba(15,23,42,0.1)' : 'action.selected',
                  },
                }}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        ) : null}

        <Box
          className="lms-protected-content"
          sx={{
            position: 'relative',
            zIndex: 0,
            ...contentProtectionSx,
            width: '100%',
            ...(isFullscreen
              ? {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  px: { xs: 1.5, sm: 2 },
                  pb: { xs: 1.5, sm: 2 },
                  pt: showFullscreenControl ? 0 : { xs: 1.5, sm: 2 },
                  bgcolor: '#ffffff',
                }
              : {}),
          }}
        >
          <ContentProtectionWatermark label={watermark} />
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              width: '100%',
              ...(isFullscreen
                ? {
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }
                : {}),
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </FullscreenContentContext.Provider>
  );
}

/**
 * Helper for child players to stretch inside fullscreen frame.
 */
export function FullscreenFillContainer({ children, sx = {} }) {
  const isFullscreen = useFullscreenContent();

  return (
    <Box
      sx={{
        width: '100%',
        ...(isFullscreen
          ? {
              flex: 1,
              minHeight: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }
          : {}),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
